import { ChangeDetectionStrategy, Component } from '@angular/core';

import { CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { LocalStoreService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { Observable, distinctUntilChanged, map, tap } from 'rxjs';

import { IBetslipRootState } from '../../../../base/store/state';
import { RewardTokensActions } from '../../actions';
import { RewardTokensStore } from '../../reward-tokens-store.model';
import { AvailableTokensType, IOnboardingViewData, IRewardToken } from '../../reward-tokens.model';
import { selectRewardTokensOnboardingDisplayState } from '../../selectors';
import { getNonAccaTokens, getTokensType } from '../../services/reward-tokens.utils';

@Component({
    selector: 'bs-reward-tokens-onboarding-container',
    templateUrl: './reward-tokens-onboarding-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class RewardTokensOnboardingContainerComponent {
    viewState$: Observable<IOnboardingViewData | null>;

    constructor(
        public sitecore: Sitecore,
        private tokensConfig: CrmConfig,
        private localStore: LocalStoreService,
        private store: Store<IBetslipRootState>,
    ) {
        this.viewState$ = this.store.select(selectRewardTokensOnboardingDisplayState).pipe(
            distinctUntilChanged((prev, next) => prev.isVisible === next.isVisible && prev.tokens.length === next.tokens.length),
            map((state) => (state.isVisible ? this.getOnboardingViewData(state.tokens) : null)),
            tap((model) => !!model && this.setWasOnboardingShownToStore()),
        );
    }

    private getOnboardingViewData(eligibleTokens: IRewardToken[]): IOnboardingViewData | null {
        // filter out acca boost tokens
        const tokens = getNonAccaTokens(eligibleTokens);

        if (!tokens.length) {
            return null;
        }

        const availableTokensType = getTokensType(tokens) as Exclude<AvailableTokensType, AvailableTokensType.AccaBoost>;

        const messages = this.sitecore.crmTokens.tokensOnboardingMessages;
        if (!messages) {
            return { title: '', content: '', isNew: false, icon: '' };
        }

        switch (availableTokensType) {
            case AvailableTokensType.OddsBoost: {
                return <IOnboardingViewData>{
                    title: messages.OddsBoostTitle,
                    content: messages.OddsBoostContent,
                    icon: this.tokensConfig.tokens.icons.oddsBoost,
                    isNew: true,
                };
            }
            case AvailableTokensType.RiskFreeBet: {
                return <IOnboardingViewData>{
                    title: messages.RiskFreeTitle,
                    content: messages.RiskFreeContent,
                    icon: this.tokensConfig.tokens.icons.riskFree,
                    isNew: true,
                };
            }
            case AvailableTokensType.FreeBet: {
                return <IOnboardingViewData>{
                    title: messages.FreebetTitle,
                    content: messages.FreebetContent,
                    icon: this.tokensConfig.tokens.icons.freeBet,
                    isNew: false,
                };
            }
            case AvailableTokensType.BetAndGet: {
                return <IOnboardingViewData>{
                    title: messages.BetAndGetTitle,
                    content: messages.BetAndGetContent,
                    icon: this.tokensConfig.tokens.icons.betAndGet,
                    isNew: false,
                };
            }
            case AvailableTokensType.MultipleRewards: {
                return <IOnboardingViewData>{
                    title: messages.MultipleRewardsTitle,
                    content: messages.MultipleRewardsContent,
                    isNew: true,
                };
            }
        }
    }

    private setWasOnboardingShownToStore(): void {
        this.localStore.set<RewardTokensStore>('rewardTokens', { onboardingMessageShown: true });
    }

    // it is being used in template
    readonly hideOnboarding = () => this.store.dispatch(RewardTokensActions.hideOnboarding());
}
